"use client";
import { ChartConfig, ChartContainer } from "@/components/ui/chart";
import { Item, ItemTitle } from "@/components/ui/item";
import { numberUsLocale } from "@/lib";
import { ItemType } from "@/types";
import { Bar, BarChart, CartesianGrid, LabelList, LabelProps, XAxis, YAxis } from "recharts";
import { ProductOrdersLink } from "../ProductOrdersLink";

const chartConfig = {
  count: {
    label: "Count",
    color: "var(--chart-2)",
  },
  label: {
    color: "var(--background)",
  },
} satisfies ChartConfig;

type HorizontalChartProps = {
  chartData: ItemType[];
  color?: string;
  maxCount: number;
  title?: string;
};

export const ProductsHorizontalChart = ({
  chartData,
  color,
  maxCount,
  title,
}: HorizontalChartProps) => {
  return (
    <Item variant="outline">
      <ItemTitle>{title}</ItemTitle>
      <ChartContainer config={chartConfig} className="chart-container">
        <BarChart accessibilityLayer data={chartData} layout="vertical">
          <CartesianGrid horizontal={false} />
          <YAxis
            dataKey="count"
            type="category"
            tickLine={false}
            axisLine={false}
            orientation="right"
            fontSize={16}
            fontWeight={600}
            tickFormatter={(volume: number) => numberUsLocale(Math.round(volume))}
          />
          <XAxis
            type="number"
            dataKey="count"
            domain={[0, maxCount]}
            tickMargin={4}
            tickFormatter={(volume: number) => numberUsLocale(volume)}
          />
          <Bar
            dataKey="count"
            layout="vertical"
            fill={color || "var(--color-chart-2)"}
            radius={4}
            barSize={20}
            background={{ fill: "var(--color-muted)" }}
          >
            <LabelList
              dataKey="productName"
              content={(props: LabelProps) => {
                const { x, y, value, index } = props;
                if (typeof x !== "number" || typeof y !== "number" || typeof index !== "number") {
                  return null;
                }

                const item = chartData[index];
                if (!item) return null;

                return (
                  <foreignObject x={x + 4} y={y - 24} width={"100%"} height={24}>
                    <ProductOrdersLink productId={item.productId} productName={value as string} />
                  </foreignObject>
                );
              }}
            />
            <LabelList
              dataKey="productType"
              position="insideLeft"
              offset={4}
              className="fill-background capitalize font-semibold"
              fontSize={12}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </Item>
  );
};
